import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, X, Send, Minimize2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SupportMessage {
  id: string;
  sender_type: string;
  sender_name: string | null;
  message: string;
  created_at: string;
}

interface SupportChat {
  id: string;
  status: string;
  subject: string | null;
  created_at: string;
}

export const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentChat, setCurrentChat] = useState<SupportChat | null>(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    subject: ""
  });
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Check for existing chat on component mount
  useEffect(() => {
    loadUserInfo();
    checkExistingChat();
  }, []);

  // Load user information automatically
  const loadUserInfo = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('user_id', user.user.id)
        .single();

      if (profile) {
        setCustomerInfo(prev => ({
          ...prev,
          name: profile.full_name || '',
          email: profile.email || user.user.email || ''
        }));
      } else {
        // Fallback to user metadata
        setCustomerInfo(prev => ({
          ...prev,
          name: user.user.user_metadata?.full_name || '',
          email: user.user.email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollAreaRef.current) {
        const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollElement) {
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      }
    };
    
    // Use setTimeout to ensure DOM has updated
    setTimeout(scrollToBottom, 100);
  }, [messages]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!currentChat) return;

    const channel = supabase
      .channel(`support_chat_${currentChat.id}-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `chat_id=eq.${currentChat.id}`
        },
        (payload) => {
          const newMessage = payload.new as SupportMessage;
          setMessages(prev => [...prev, newMessage]);
          
          // Show notification for admin replies
          if (newMessage.sender_type === 'admin') {
            toast({
              title: "New reply from support",
              description: newMessage.message.substring(0, 50) + "...",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentChat, toast]);

  const checkExistingChat = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      // Check for existing open chat
      const { data: chats } = await supabase
        .from('support_chats')
        .select('*')
        .eq('customer_email', user.user.email)
        .eq('is_closed', false)
        .order('created_at', { ascending: false })
        .limit(1);

      if (chats && chats.length > 0) {
        setCurrentChat(chats[0]);
        setHasStartedChat(true);
        loadMessages(chats[0].id);
      }
    } catch (error) {
      console.error('Error checking existing chat:', error);
    }
  };

  const loadMessages = async (chatId: string) => {
    try {
      const { data } = await supabase
        .from('support_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (data) {
        setMessages(data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const startChat = async () => {
    if (!customerInfo.subject.trim()) {
      toast({
        title: "Please enter a subject",
        description: "Subject is required to start a chat.",
        variant: "destructive"
      });
      return;
    }

    // Auto-populate name and email if not already set
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      toast({
        title: "Authentication required",
        description: "Please log in to start a chat.",
        variant: "destructive"
      });
      return;
    }

    let finalName = customerInfo.name;
    let finalEmail = customerInfo.email;

    if (!finalName || !finalEmail) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('user_id', user.user.id)
        .single();

      finalName = finalName || profile?.full_name || user.user.user_metadata?.full_name || 'Anonymous';
      finalEmail = finalEmail || profile?.email || user.user.email || '';
    }

    if (!finalEmail) {
      toast({
        title: "Email required",
        description: "Unable to determine your email address.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      // Create new chat
      const { data: chat, error: chatError } = await supabase
        .from('support_chats')
        .insert({
          customer_email: finalEmail,
          customer_name: finalName,
          subject: customerInfo.subject,
          status: 'new'
        })
        .select()
        .single();

      if (chatError) throw chatError;

      // Send initial message
      const { error: messageError } = await supabase
        .from('support_messages')
        .insert({
          chat_id: chat.id,
          sender_type: 'customer',
          sender_name: finalName,
          sender_email: finalEmail,
          message: `Subject: ${customerInfo.subject}\n\nHi, I need help with the above topic.`
        });

      if (messageError) throw messageError;

      setCurrentChat(chat);
      setHasStartedChat(true);
      setCustomerInfo(prev => ({ ...prev, name: finalName, email: finalEmail }));
      loadMessages(chat.id);
      
      toast({
        title: "Chat started!",
        description: "A support agent will respond to you shortly.",
      });
    } catch (error) {
      console.error('Error starting chat:', error);
      toast({
        title: "Error starting chat",
        description: "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentChat) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('support_messages')
        .insert({
          chat_id: currentChat.id,
          sender_type: 'customer',
          sender_name: customerInfo.name,
          sender_email: customerInfo.email,
          message: newMessage.trim()
        });

      if (error) throw error;

      setNewMessage("");
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error sending message",
        description: "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (hasStartedChat) {
        sendMessage();
      } else {
        startChat();
      }
    }
  };

  if (!isOpen) {
  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Button
        onClick={() => setIsOpen(true)}
        className="h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
        size="icon"
        aria-label="Open live support chat"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    </div>
  );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Card className={`w-80 shadow-xl transition-all duration-200 ${isMinimized ? 'h-12' : 'h-96'}`}>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => setIsMinimized(!isMinimized)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Live Support
              {currentChat && (
                <Badge variant="secondary" className="text-xs">
                  {currentChat.status}
                </Badge>
              )}
            </CardTitle>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(!isMinimized);
                }}
                aria-label={isMinimized ? "Maximize chat" : "Minimize chat"}
              >
                <Minimize2 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                aria-label="Close chat"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {!isMinimized && (
          <CardContent className="p-3">
            {!hasStartedChat ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Input
                    placeholder="Your name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                    onKeyPress={handleKeyPress}
                    disabled={!!customerInfo.name} // Disable if auto-populated
                  />
                  <Input
                    placeholder="Your email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                    onKeyPress={handleKeyPress}
                    disabled={!!customerInfo.email} // Disable if auto-populated
                  />
                  <Input
                    placeholder="Subject (what do you need help with?)"
                    value={customerInfo.subject}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, subject: e.target.value }))}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <Button 
                  onClick={startChat} 
                  disabled={isLoading || !customerInfo.subject.trim()}
                  className="w-full"
                >
                  {isLoading ? "Starting..." : "Start Chat"}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <ScrollArea className="h-52" ref={scrollAreaRef}>
                  <div className="space-y-2 pr-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] p-2 rounded-lg text-sm ${
                            message.sender_type === 'customer'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <div className="font-medium text-xs mb-1">
                            {message.sender_type === 'customer' ? 'You' : 'Support'}
                          </div>
                          <div className="whitespace-pre-wrap">{message.message}</div>
                          <div className="text-xs opacity-70 mt-1">
                            {new Date(message.created_at).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    aria-label="Type your message"
                  />
                  <Button
                    size="icon"
                    onClick={sendMessage}
                    disabled={isLoading || !newMessage.trim()}
                    aria-label="Send message"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};